import React from "react";

import { Language } from "@mui/icons-material";
import { Box, Button, ButtonGroup, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors } from "@rubi/ui";

import { localeToLanguage } from "@/common/utils";

interface TranslationToggleProps {
  locale: string;
  showTranslated: boolean;
  onChange: (showTranslated: boolean) => void;
  disabled?: boolean;
}

const toggleButtonSx = (active: boolean) => ({
  backgroundColor: active ? "primary.main" : "transparent",
  color: active ? colors.common.white : "primary.main",
  "&:hover": {
    backgroundColor: active ? "primary.dark" : alpha(colors.common.black, 0.04),
  },
});

const TranslationToggle: React.FC<TranslationToggleProps> = ({
  locale,
  showTranslated,
  onChange,
  disabled = false,
}) => {
  const language = localeToLanguage[locale] || locale;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "12px",
        padding: "8px 12px",
        borderRadius: "8px",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontStyle: "italic" }}
      >
        {showTranslated
          ? `Auto-translated from: ${language}`
          : `Detected Languages: ${language}`}
      </Typography>
      <Language color="action" />
      <ButtonGroup variant="contained" size="small" disabled={disabled}>
        <Tooltip title="View in English translation">
          <Button
            onClick={() => onChange(true)}
            variant={showTranslated ? "contained" : "outlined"}
            sx={toggleButtonSx(showTranslated)}
          >
            Translated
          </Button>
        </Tooltip>
        <Tooltip title="View original submission">
          <Button
            onClick={() => onChange(false)}
            variant={!showTranslated ? "contained" : "outlined"}
            sx={toggleButtonSx(!showTranslated)}
          >
            Original
          </Button>
        </Tooltip>
      </ButtonGroup>
    </Box>
  );
};

export default TranslationToggle;
